// @ts-strict-ignore
import { getCdnApiBaseUrl } from '../../modules/platform'
import { SQL_JS_CONFIG } from 'common_lib/db/sqlite'
import L from 'leaflet'
import initSqlJs from 'sql.js'

export interface MapPinIconStyle {
	style?: string
	color?: string
	icon?: string
	padding?: boolean
	resize?: number
}

const fetchLocal = (url: string): Promise<Response> =>
	new Promise<Response>((resolve, reject) => {
		const xhr = new XMLHttpRequest()
		xhr.responseType = 'arraybuffer'

		xhr.onload = function (): void {
			resolve(new Response(xhr.response, { status: xhr.status }))
		}
		xhr.onerror = function (): void {
			reject(new TypeError('Local request failed'))
		}
		xhr.open('GET', url)
		xhr.send(null)
	})

export const getValueFromTable = (metaStmt: any, key: string, processor?: (x: any) => any) => {
	const value = metaStmt.getAsObject({ ':key': key })
	return processor?.(value?.value)
}

export const getStringDatabase = (databaseUrl: string): Promise<ArrayBuffer> => {
	if (L.Browser.android) {
		return fetchLocal(databaseUrl).then(response => response.arrayBuffer())
	}

	return fetch(databaseUrl).then(response => response.arrayBuffer())
}

export const processBounds = (bounds: string) => {
	const processed = bounds.split(',').map(Number)
	return [processed.slice(0, 2), processed.slice(2)]
}

const calculateCenterFromBounds = (bounds: number[][]) => {
	try {
		const lat = (bounds[0][0] + bounds[1][0]) / 2
		const lng = (bounds[0][1] + bounds[1][1]) / 2

		return { lat, lng }
	} catch (error) {
		return {}
	}
}

interface Metadata {
	center: [number, number]
	minZoom?: number
	maxZoom?: number
}

export const getMBTilesMetadata = async (databaseUrl: string): Promise<Metadata> => {
	if (!databaseUrl) {
		// return empty data when no databaseUrl
		return { center: undefined }
	}

	if (!databaseUrl) {
		throw 'no database url provided'
	}

	const SQL = await initSqlJs(SQL_JS_CONFIG)
	const buffer = await getStringDatabase(databaseUrl)
	const uInt8Array = new Uint8Array(buffer)
	const db = new SQL.Database(uInt8Array)

	const metaStmt = db.prepare('SELECT value FROM metadata WHERE name = :key')

	const bounds = getValueFromTable(metaStmt, 'bounds', processBounds)

	const { lat, lng } = calculateCenterFromBounds(bounds)

	const minZoom = getValueFromTable(metaStmt, 'minzoom', Number)

	const maxZoom = getValueFromTable(metaStmt, 'maxzoom', Number)

	return {
		minZoom,
		maxZoom,
		center: [lng, lat],
	}
}

export const generateMapPinIconUrl = ({ style, color, icon, padding, resize }: MapPinIconStyle): string => {
	// use production CDN host for development to make map pins available
	const cdnUrl =
		import.meta.env.MODE === 'development'
			? 'https://app.mytoursapp.com'
			: getCdnApiBaseUrl()
	const queryParams = new URLSearchParams({ padding: padding ? 'true' : '', resize: resize ? resize.toString() : '' })
	return `${cdnUrl}/api/v3/images/map_pin/${style || 'default'}/${color || 'default'}/${
		icon || 'default'
	}.png?${queryParams.toString()}`
}

export const createLeafletMarkerIcon = (style: string, color: string, icon: string) => {
	const markerIcon = L.icon({
		iconUrl: generateMapPinIconUrl({ style, color, icon }),
		iconSize: [49, 53],
		iconAnchor: [25, 53],
	})

	return markerIcon
}
